import React, { useState, useEffect } from 'react';
import { ConfigProvider, DatePicker, Input, message } from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/en_US';
import './threshold.css';
import { BASE_URL } from '../apiConfig';
import Loading  from './loading';

// Custom ThresholdInput component
const ThresholdInput = ({ id, value, type, column_data_type, onChange, hasError }) => {
    const getInputProps = () => {
        switch (column_data_type) {
            case 'numeric_with_percentage':
                return { suffix: '%' };
            case 'numeric_currency':
                return { prefix: '₹' };
            default:
                return {};
        }
    };

    return (
        <div className="threshold-input-container">
            <Input
                className={`threshold-input ${hasError ? 'error-input' : ''}`}
                value={value}
                onChange={(e) => onChange(id, e.target.value, type, column_data_type)}
                status={hasError ? "error" : ""}
                {...getInputProps()}
            />
        </div>
    );
};

const ThresholdComponent = () => {
    const [thresholdData, setThresholdData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formValues, setFormValues] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [inputStatus, setInputStatus] = useState(null); 
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const inputID = location.state?.inputID || null; 
    const clientID = location.state?.clientID || null;
    const roleId = location.state?.roleId || null;
    const dataUploaded = location.state?.dataUploaded || false;
    useEffect(() => {
        fetchThresholdData();
        console.log('Client ID:', clientID);
    }, []);

    const fetchThresholdData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            // Fetch threshold details
            const detailsResponse = await fetch(`${BASE_URL}/project/get_project_threshold_details?project_id=${projectId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!detailsResponse.ok) {
                throw new Error('Failed to fetch threshold details');
            }

            const detailsData = await detailsResponse.json();
            

            // Fetch threshold values
            const valuesResponse = await fetch(`${BASE_URL}/project/get_project_threshold_values?project_id=${projectId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            let thresholdValues = [];
            if (valuesResponse.ok) {
                thresholdValues = await valuesResponse.json();
            }

            // Initialize form values
            const minValues = {};
            const maxValues = {};

            detailsData.forEach((item) => {
                const matchingValue = thresholdValues.find(val => val.column_id === item.id);

                if (matchingValue) {
                    // If values exist, use them (convert dates to YYYY-MM-DD if needed)
                    if (item.column_data_type === 'date') {
                        minValues[item.id] = matchingValue.min_value ? dayjs(matchingValue.min_value).format('YYYY-MM-DD') : '';
                        maxValues[item.id] = matchingValue.max_value ? dayjs(matchingValue.max_value).format('YYYY-MM-DD') : '';
                    } else {
                        // For numeric values, convert to string and format with commas if needed
                        minValues[item.id] = matchingValue.min_value != null ?
                            matchingValue.min_value.toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',') : '';
                        maxValues[item.id] = matchingValue.max_value != null ?
                            matchingValue.max_value.toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',') : '';
                    }
                }

                else {

                    minValues[item.id] = '';
                    maxValues[item.id] = '';
                }
            });

            setThresholdData(detailsData);
            setFormValues({ min: minValues, max: maxValues });
            setLoading(false);
        } catch (error) {
            message.error('Failed to fetch threshold data');
            console.error('Error fetching threshold data:', error);
            setLoading(false);
        }
    };

    // Validate form before submission
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Check if all mandatory fields have values and validate min/max relationship
        thresholdData.forEach((item) => {
            const itemId = item.id;
            if (!newErrors[itemId]) newErrors[itemId] = {};

            // Check if mandatory fields are filled
            if (item.is_mandatory) {
                if (!formValues.min[itemId] || formValues.min[itemId].trim() === '') {
                    newErrors[itemId].min = 'This field is required';
                    isValid = false;
                }

                if (!formValues.max[itemId] || formValues.max[itemId].trim() === '') {
                    newErrors[itemId].max = 'This field is required';
                    isValid = false;
                }
            }

            // If both values are present, check if min <= max
            if (formValues.min[itemId] && formValues.max[itemId] &&
                formValues.min[itemId].trim() !== '' && formValues.max[itemId].trim() !== '') {

                if (item.column_data_type === 'date') {
                    // For date type, compare as dates
                    const minDate = dayjs(formValues.min[itemId]);
                    const maxDate = dayjs(formValues.max[itemId]);

                    if (minDate.isValid() && maxDate.isValid() && minDate.isAfter(maxDate)) {
                        newErrors[itemId].min = 'Min date cannot be greater than max date';
                        newErrors[itemId].max = 'Max date cannot be less than min date';
                        isValid = false;
                    }
                } else {
                    // For numeric types, compare as numbers
                    const minVal = parseFloat(formValues.min[itemId].replace(/,/g, ''));
                    const maxVal = parseFloat(formValues.max[itemId].replace(/,/g, ''));

                    if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
                        newErrors[itemId].min = 'Min value cannot be greater than max value';
                        newErrors[itemId].max = 'Max value cannot be less than min value';
                        isValid = false;
                    }
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            message.error({
                content: 'Please fix the validation errors',
                style: { color: '#C12328' }
            });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('accessToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            // Prepare request body
            const requestBody = thresholdData.map(item => {
                const minValue = formValues.min[item.id]?.replace(/,/g, '') || "0";
                const maxValue = formValues.max[item.id]?.replace(/,/g, '') || "0";

                return {
                    column_id: item.id,
                    column_type: item.column_data_type,
                    min_value: item.column_data_type === 'date' ? formValues.min[item.id] : parseFloat(minValue),
                    max_value: item.column_data_type === 'date' ? formValues.max[item.id] : parseFloat(maxValue),
                };
            });

            // Make API call
            const response = await fetch(`${BASE_URL}/project/add_update_threshold_value?project_id=${projectId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error('Failed to submit threshold data');
            }

            const responseData = await response.json();
            
            // Extract input status from the response
            const latestInputStatus = responseData?.latest_input_model?.input_status || null;
            setInputStatus(latestInputStatus);

            message.success('Threshold data saved successfully');
            console.log('inputID:', inputID, 'Type:', typeof (inputID));
            console.log('Input Status:', latestInputStatus);
            
            const hasUploadError =
                latestInputStatus === "data_upload_unsuccessful" ||
                latestInputStatus === "uploaded_data_has_errors";

            if (inputID === null || dataUploaded === false || hasUploadError) {
                navigate(`/architect/${projectId}/upload`, {
                    state: {
                        inputStatus: latestInputStatus,
                        inputID,
                        projectId,
                        clientID,
                        roleId,
                    }
                });
            }
            else {
                navigate(`/architect/${inputID}/summary`, {
                    state: { inputStatus: latestInputStatus, projectId: projectId, clientID: clientID, roleId: roleId }
                });
            }

        } catch (error) {
            message.error('Failed to submit threshold data');
            console.error('Error submitting threshold data:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (id, value, type, dataType) => {
        // Clear error for this field when user types
        if (errors[id] && errors[id][type]) {
            setErrors(prev => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    [type]: undefined
                }
            }));

            // Also clear the opposing field's error if it was a min/max relationship error
            const oppositeType = type === 'min' ? 'max' : 'min';
            if (errors[id] && errors[id][oppositeType] &&
                (errors[id][oppositeType].includes('cannot be greater') ||
                    errors[id][oppositeType].includes('cannot be less'))) {
                setErrors(prev => ({
                    ...prev,
                    [id]: {
                        ...prev[id],
                        [oppositeType]: undefined
                    }
                }));
            }
        }

        if (dataType === 'numeric_with_percentage' || dataType === 'numeric_currency' || dataType === 'numeric') {
            // Remove all non-numeric characters except for the decimal point
            value = value.replace(/[^\d.]/g, '');

            // Format with commas for Indian currency system
            if (value) {
                const parts = value.split('.');
                parts[0] = parts[0].replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ',');
                value = parts.join('.');
            }
        }

        setFormValues((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [id]: value,
            },
        }));
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return '';
        const birthDate = dayjs(dateOfBirth);
        const now = dayjs();
        const years = now.diff(birthDate, 'year');
        return `${years} years`;
    };

    const handleBack = () => {
        navigate(`/architect/${clientID}/${roleId}/${projectId}/edit-project`);
    };

    const renderInputField = (item, type) => {
        const { id, column_data_type } = item;
        const value = formValues[type]?.[id] || '';
        const hasError = errors[id] && errors[id][type] !== undefined;

        if (column_data_type === 'date') {
            return (
                <div className="threshold-date-field">
                    <DatePicker
                        className={`threshold-date-picker ${hasError ? 'error-input' : ''}`}
                        locale={locale}
                        format="DD-MM-YYYY"
                        onChange={(date) => {
                            // Clear error
                            if (errors[id] && errors[id][type]) {
                                setErrors(prev => ({
                                    ...prev,
                                    [id]: {
                                        ...prev[id],
                                        [type]: undefined
                                    }
                                }));

                                // Also clear the opposing field's error if it was a min/max relationship error
                                const oppositeType = type === 'min' ? 'max' : 'min';
                                if (errors[id] && errors[id][oppositeType] &&
                                    (errors[id][oppositeType].includes('cannot be greater') ||
                                        errors[id][oppositeType].includes('cannot be less'))) {
                                    setErrors(prev => ({
                                        ...prev,
                                        [id]: {
                                            ...prev[id],
                                            [oppositeType]: undefined
                                        }
                                    }));
                                }
                            }

                            setFormValues((prev) => ({
                                ...prev,
                                [type]: {
                                    ...prev[type],
                                    [id]: date ? date.format('YYYY-MM-DD') : '',
                                },
                            }));
                        }}
                        value={value ? dayjs(value) : null}
                        // status={hasError ? "error" : ""}
                        status={hasError ? "error" : ""}
                        popupStyle={{ width: '320px', height: 'auto' }}
                        style={{ width: '100%' }}
                        dropdownClassName="custom-datepicker-dropdown"
                    />
                    <div className="threshold-age-wrapper">
                        <div className="threshold-age-label">{type === 'min' ? 'Max. Age' : 'Min. Age'}</div>
                        <div className="threshold-age-value">{value ? calculateAge(value) : ''}</div>
                    </div>
                </div>
            );
        }

        return (
            <ThresholdInput
                id={id}
                value={value}
                type={type}
                column_data_type={column_data_type}
                onChange={handleInputChange}
                hasError={hasError}
            />
        );
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Input: {
                        borderRadius: 4,
                        borderColor: '#858585',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 15,
                        fontWeightStrong: 500,
                        paddingBlock: 8,
                        paddingInline: 11,
                        hoverBorderColor: '#C12328',
                        activeBorderColor: '#C12328',
                        // activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
                    },
                    DatePicker: {
                        borderRadius: 4,
                        borderColor: '#858585',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 15,
                        hoverBorderColor: '#C12328',
                        activeBorderColor: '#C12328',
                    },
                },
            }}
        >
            <div className="threshold-main-container">
                <div className="threshold-content">
                    <div className="threshold-header">
                        <div className="threshold-back-button" onClick={handleBack}>
                            <LeftOutlined /> Project Thresholds
                        </div>
                    </div>
                    {loading ? (
                        // <div className="threshold-loading">Loading thresholds...</div>
                        <Loading />
                    ) : (
                        <div className="threshold-card">
                            <div className="threshold-columns-header">
                                <div className="threshold-field-column"></div>
                                <div className="threshold-values-columns">
                                    <div className="threshold-min-column">Minimum</div>
                                    <div className="threshold-max-column">Maximum</div>
                                </div>
                            </div>

                            {thresholdData.map((item) => (
                                <div className="threshold-row" key={item.id}>
                                    <div className="threshold-field-name">
                                        {item.client_column_name}
                                        {item.is_mandatory && <span className="threshold-mandatory">*</span>}
                                    </div>
                                    <div className="threshold-values">
                                        <div className="threshold-min-value">
                                            {renderInputField(item, 'min')}
                                            {errors[item.id] && errors[item.id].min && (
                                                <div className="error-message" style={{ color: '#C12328', fontSize: '12px', marginTop: '4px' }}>
                                                    {errors[item.id].min}
                                                </div>
                                            )}
                                        </div>
                                        <div className="threshold-max-value">
                                            {renderInputField(item, 'max')}
                                            {errors[item.id] && errors[item.id].max && (
                                                <div className="error-message" style={{ color: '#C12328', fontSize: '12px', marginTop: '4px' }}>
                                                    {errors[item.id].max}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="threshold-footer">
                <button
                    className="threshold-next-button"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting...' : 'Next'}
                </button>
            </div>
        </ConfigProvider>
    );
};

export default ThresholdComponent;